import { canv } from "@/canv/global";
import { getViewport } from "@/canvasController/helpers";
import type { elementBase } from "@/elementBase/elementBase";
import { ShapeWidget } from "@/elements/shape";
import { textWidget } from "@/elements/text/text";
import { eventEmitter } from "@/eventEmitter";
import type { Group } from "@/group";
import { getScaledSize } from "@/utils";
import {
    AVAILABLE_WIDGETS_NAMES_FOR_VOTE,
    HORIZONTAL_PADDING,
    RECT_FOR_CORNER_SHOW_SIZE,
    RECT_FOR_CORNER_SIZE,
    VERTICAL_PADDING,
    VOTE_TOOLTIP_HEIGHT,
    VOTE_TOOLTIP_MARGIN,
    VOTE_TOOLTIP_WIDTH,
} from "./";

export class VoteScreen {
    create(element: HTMLElement) {
        this.setPositionRect(element);

        eventEmitter.dispatch("toolBox.visibility", { isVisible: false });
        canv.toolBox.switchToByName("voteTool");

        this.update();

        this.candidates.forEach((group) => {
            this.selectGroup(group);
        });
    }

    getRatio() {
        const viewPort = getViewport();

        return canv.canvas.width / viewPort.viewportWidth;
    }

    setPositionRect(element: HTMLElement) {
        const viewPort = getViewport();
        const ratio = this.getRatio();
        const { left } = element.getBoundingClientRect();

        this.startX = viewPort.viewportStartX + HORIZONTAL_PADDING / ratio;
        this.startY = viewPort.viewportStartY + VERTICAL_PADDING / ratio;
        this.width = (left - HORIZONTAL_PADDING * 2) / ratio;
        this.height = viewPort.viewportHeight - (VERTICAL_PADDING * 2) / ratio;
    }

    remove() {
        canv.ctx.save();
        canv.ctx.clearRect(this.startX, this.startY, this.width, this.height);
        canv.ctx.restore();

        eventEmitter.dispatch("toolBox.visibility", { isVisible: true });
        canv.toolBox.switchToByName("cursorTool");

        this.selectedGroupUIDs.clear();
        this.candidates.clear();

        canv.redraw();
    }

    getCornersCoords() {
        const ratio = this.getRatio();
        const fullSize = RECT_FOR_CORNER_SIZE / ratio;
        const cutSize = RECT_FOR_CORNER_SHOW_SIZE / ratio;

        return {
            leftTop: {
                startX: this.startX - RECT_FOR_CORNER_SHOW_SIZE / ratio,
                startY: this.startY - RECT_FOR_CORNER_SHOW_SIZE / ratio,
            },
            rightTop: {
                startX:
                    this.startX +
                    this.width -
                    (RECT_FOR_CORNER_SIZE - RECT_FOR_CORNER_SHOW_SIZE) / ratio,
                startY: this.startY - RECT_FOR_CORNER_SHOW_SIZE / ratio,
            },
            leftBottom: {
                startX: this.startX - RECT_FOR_CORNER_SHOW_SIZE / ratio,
                startY:
                    this.startY +
                    this.height -
                    (RECT_FOR_CORNER_SIZE - RECT_FOR_CORNER_SHOW_SIZE) / ratio,
            },
            rightBottom: {
                startX:
                    this.startX +
                    this.width -
                    (RECT_FOR_CORNER_SIZE - RECT_FOR_CORNER_SHOW_SIZE) / ratio,
                startY:
                    this.startY +
                    this.height -
                    (RECT_FOR_CORNER_SIZE - RECT_FOR_CORNER_SHOW_SIZE) / ratio,
            },
            fullSize,
            cutSize,
        };
    }

    update() {
        this.checkGroupsInScreen();

        canv.redraw();
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;

        this.update();
    }

    resize(catchPointer: string) {
        const pt = canv.actionManager.getActionPoint();
        const { fullSize } = this.getCornersCoords();

        let startX = this.startX;
        let startY = this.startY;
        let width = this.width;
        let height = this.height;

        if (catchPointer === "leftTop") {
            startX = pt.x;
            width = this.startX + this.width - startX;
            startY = pt.y;
            height = this.startY + this.height - startY;
        } else if (catchPointer === "rightTop") {
            startY = pt.y;
            height = this.startY + this.height - startY;
            width = pt.x - this.startX;
        } else if (catchPointer === "leftBottom") {
            startX = pt.x;
            width = this.startX + this.width - startX;
            height = pt.y - this.startY;
        } else if (catchPointer === "rightBottom") {
            width = pt.x - this.startX;
            height = pt.y - this.startY;
        }

        if (width >= fullSize && height >= fullSize) {
            this.startX = startX;
            this.startY = startY;
            this.width = width;
            this.height = height;

            this.update();
        }
    }

    overCorner() {
        const pt = canv.actionManager.getActionPoint();
        const { leftTop, rightTop, leftBottom, rightBottom, fullSize } = this.getCornersCoords();

        if (
            pt.x > leftTop.startX &&
            pt.y > leftTop.startY &&
            pt.x < leftTop.startX + fullSize &&
            pt.y < leftTop.startY + fullSize
        ) {
            return "leftTop";
        }

        if (
            pt.x > rightTop.startX &&
            pt.y > rightTop.startY &&
            pt.x < rightTop.startX + fullSize &&
            pt.y < rightTop.startY + fullSize
        ) {
            return "rightTop";
        }

        if (
            pt.x > leftBottom.startX &&
            pt.y > leftBottom.startY &&
            pt.x < leftBottom.startX + fullSize &&
            pt.y < leftBottom.startY + fullSize
        ) {
            return "leftBottom";
        }

        if (
            pt.x > rightBottom.startX &&
            pt.y > rightBottom.startY &&
            pt.x < rightBottom.startX + fullSize &&
            pt.y < rightBottom.startY + fullSize
        ) {
            return "rightBottom";
        }

        return null;
    }

    isOverRect() {
        const pt = canv.actionManager.getActionPoint();

        return (
            pt.x > this.startX &&
            pt.y > this.startY &&
            pt.x < this.startX + this.width &&
            pt.y < this.startY + this.height
        );
    }

    drawTooltip() {
        const startX = this.startX + getScaledSize(VOTE_TOOLTIP_MARGIN);
        const startY = this.startY + getScaledSize(VOTE_TOOLTIP_MARGIN);
        const width = getScaledSize(VOTE_TOOLTIP_WIDTH);
        const height = getScaledSize(VOTE_TOOLTIP_HEIGHT);
        const fontSize = (getScaledSize(9) * canv.dpr) / 1.5;

        canv.ctx.save();

        canv.ctx.beginPath();
        canv.ctx.fillStyle = "rgba(44, 45, 50, 1)";
        canv.ctx.roundRect(startX, startY, width, height, getScaledSize(8));
        canv.ctx.fill();

        canv.ctx.font = `normal ${fontSize.toFixed(4)}px Rubik`;
        canv.ctx.fillStyle = "rgba(250, 250, 250, 1)";
        canv.ctx.textAlign = "center";
        canv.ctx.textBaseline = "middle";
        canv.ctx.fillText("Область голосования", startX + width / 2, startY + height / 2);

        canv.ctx.restore();
    }

    drawBackground() {
        const viewPort = getViewport();

        canv.ctx.save();

        canv.ctx.beginPath();
        canv.ctx.rect(this.startX, this.startY, this.width, this.height);
        canv.ctx.rect(
            viewPort.viewportStartX,
            viewPort.viewportStartY,
            viewPort.viewportWidth,
            viewPort.viewportHeight,
        );
        canv.ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
        canv.ctx.fill("evenodd");

        canv.ctx.restore();
    }

    drawCorner() {
        const { leftTop, rightTop, leftBottom, rightBottom, cutSize, fullSize } =
            this.getCornersCoords();

        canv.ctx.save();

        canv.ctx.fillStyle = "rgba(44, 45, 50, 1)";

        canv.ctx.fillRect(leftTop.startX, leftTop.startY, fullSize, cutSize);
        canv.ctx.fillRect(leftTop.startX, leftTop.startY, cutSize, fullSize);

        canv.ctx.fillRect(rightTop.startX, rightTop.startY, fullSize, cutSize);
        canv.ctx.fillRect(
            rightTop.startX + (fullSize - cutSize),
            rightTop.startY,
            cutSize,
            fullSize,
        );

        canv.ctx.fillRect(leftBottom.startX, leftBottom.startY, cutSize, fullSize);
        canv.ctx.fillRect(
            leftBottom.startX,
            leftBottom.startY + (fullSize - cutSize),
            fullSize,
            cutSize,
        );

        canv.ctx.fillRect(
            rightBottom.startX,
            rightBottom.startY + (fullSize - cutSize),
            fullSize,
            cutSize,
        );
        canv.ctx.fillRect(
            rightBottom.startX + (fullSize - cutSize),
            rightBottom.startY,
            cutSize,
            fullSize,
        );

        canv.ctx.restore();
    }

    draw() {
        canv.ctx.save();

        canv.ctx.fillStyle = "#fff";
        canv.ctx.fillRect(this.startX, this.startY, this.width, this.height);

        canv.ctx.restore();
    }

    checkGroupsInScreen() {
        this.candidates.clear();

        const candidates =
            canv.canvasController.items.filter((group) => {
                if (!group.isItemWrapper()) return false;

                const widget = group.children[0] as elementBase;

                return (
                    group.isGroupInArea(
                        {
                            x: this.startX,
                            y: this.startY,
                            width: this.width,
                            height: this.height,
                        },
                        true,
                    ) && AVAILABLE_WIDGETS_NAMES_FOR_VOTE.includes(widget.constructorName)
                );
            }) || [];

        if (candidates) {
            for (const group of candidates) {
                if (
                    group.isGroupInArea(
                        { x: this.startX, y: this.startY, width: this.width, height: this.height },
                        true,
                    )
                ) {
                    this.candidates.set(group.UID, group);
                }
            }
        }

        this.updateWidgetList();
    }

    selectGroup(group: Group) {
        if (this.selectedGroupUIDs.has(group.UID)) {
            this.selectedGroupUIDs.delete(group.UID);
        } else {
            this.selectedGroupUIDs.add(group.UID);
        }

        this.updateWidgetList();
    }

    selectByType(type: string, checked = false) {
        this.candidates.forEach((candidate) => {
            if (candidate.onlyChild()?.constructorName === type) {
                if (checked) {
                    this.selectedGroupUIDs.add(candidate.UID);
                } else {
                    this.selectedGroupUIDs.delete(candidate.UID);
                }
            }
        });

        this.updateWidgetList();
        canv.redraw();
    }

    updateWidgetList() {
        canv.appInterface.voteWindow.updateWidgetList(
            Array.from(this.selectedGroupUIDs),
            Array.from(this.candidates.values()),
        );
    }

    needBorder(group: Group) {
        const child = group.onlyChild();

        return child instanceof textWidget || child instanceof ShapeWidget;
    }

    selectedGroupUIDs: Set<string> = new Set();
    candidates: Map<string, Group> = new Map();
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;
}
